import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PaginatedResponse } from '../models/paginatedResponse';
import { AutomaticCorrectionCheckType } from '../models/enums/automaticCorrectionCheckType.enum';
import { AutomaticCorrectionCheckDto } from '../models/api/automaticCorrectionCheckDto';
import { AutomaticCorrectionResultDto } from '../models/api/automaticCorrectionResultDto';
import { AutomaticCorrectionApplyResultDto } from '../models/api/automaticCorrectionApplyResultDto';
import { AutomaticCorrectionRowDeletionResultDto } from '../models/api/automaticCorrectionRowDeletionResultDto';

@Injectable({
  providedIn: 'root'
})
export class AutomaticCorrectionService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/tools/automatic-correction`;

  /**
   * GET /api/tools/automatic-correction/checks
   * Lists every check with its current bad-record count.
   */
  getChecks(): Observable<AutomaticCorrectionCheckDto[]> {
    return this.http.get<AutomaticCorrectionCheckDto[]>(`${this.apiUrl}/checks`);
  }

  /**
   * GET /api/tools/automatic-correction/checks/{checkType}/details
   * Returns a paginated list of the bad records found by a check.
   */
  getDetails(
    checkType: AutomaticCorrectionCheckType,
    pageNumber = 1,
    pageSize = 10
  ): Observable<PaginatedResponse<AutomaticCorrectionResultDto>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PaginatedResponse<AutomaticCorrectionResultDto>>(
      `${this.apiUrl}/checks/${checkType}/details`,
      { params }
    );
  }

  /**
   * GET /api/tools/automatic-correction/checks/{checkType}/export
   * Exports all bad records found by a check as CSV.
   */
  exportCsv(checkType: AutomaticCorrectionCheckType): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/checks/${checkType}/export`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * POST /api/tools/automatic-correction/checks/{checkType}/apply
   * Applies a check's automated correction. Destructive corrections require
   * confirm=true.
   */
  apply(
    checkType: AutomaticCorrectionCheckType,
    confirm = false
  ): Observable<AutomaticCorrectionApplyResultDto> {
    const params = new HttpParams().set('confirm', confirm.toString());

    return this.http.post<AutomaticCorrectionApplyResultDto>(
      `${this.apiUrl}/checks/${checkType}/apply`,
      null,
      { params }
    );
  }

  /**
   * DELETE /api/tools/automatic-correction/checks/{checkType}/rows/{rowId}
   * Deletes a single record reported by a check. 409 when something still
   * references it — the body then lists what.
   */
  deleteRow(
    checkType: AutomaticCorrectionCheckType,
    rowId: string
  ): Observable<AutomaticCorrectionRowDeletionResultDto> {
    return this.http.delete<AutomaticCorrectionRowDeletionResultDto>(
      `${this.apiUrl}/checks/${checkType}/rows/${rowId}`
    );
  }
}
